import * as yup from 'yup'
import { Checkbox } from '@/components/checkbox'
import FormField from '@/components/form-field'
import { Input, PasswordInput } from '@/components/input'
import { Textarea } from '@/components/textarea'
import type { Credencial, TiposCredenciaisResumo } from '@/types/credencial'
import CredencialForm from '../credencial-form'
import styles from '../style.module.css'

const campoCondicional = (exigido: boolean | undefined, mensagem: string) =>
  exigido
    ? yup.string().required(mensagem)
    : yup.string().defined().nullable().default('')

const buildSchema = (tipo: TiposCredenciaisResumo | null) =>
  yup.object({
    nome: yup.string().required('Preencha o nome'),
    codigoPrestador: yup.string().defined().nullable().default(''),
    login: yup.string().defined().nullable().default(''),
    senha: yup.string().defined().nullable().default(''),
    cpf: campoCondicional(tipo?.hasCpf, 'Preencha o CPF'),
    cnpj: campoCondicional(tipo?.hasCnpj, 'Preencha o CNPJ'),
    tipoLogin: campoCondicional(tipo?.hasTipoLogin, 'Preencha o tipo de login'),
    unidadeLogin: campoCondicional(tipo?.hasUnidadeLogin, 'Preencha a unidade de login'),
    nomeContratado: yup.string().defined().nullable().default(''),
    observacoes: yup.string().defined().nullable().default(''),
    visivel: yup.boolean().required(),
  })

const DadosGeraisTab = ({ credencial }: { credencial: Credencial }) => {
  const tipo = credencial.tiposCredenciais

  return (
    <CredencialForm
      credencial={credencial}
      schema={buildSchema(tipo)}
      defaultValues={{
        nome: credencial.nome ?? '',
        codigoPrestador: credencial.codigoPrestador ?? '',
        login: credencial.login ?? '',
        senha: credencial.senha ?? '',
        cpf: credencial.cpf ?? '',
        cnpj: credencial.cnpj ?? '',
        tipoLogin: credencial.tipoLogin ?? '',
        unidadeLogin: credencial.unidadeLogin ?? '',
        nomeContratado: credencial.nomeContratado ?? '',
        observacoes: credencial.observacoes ?? '',
        visivel: credencial.visivel,
      }}
      montarPayload={(dados) => ({ ...credencial, ...dados })}
    >
      {({ formState: { errors }, register }, podeEditar) => (
        <>
          <div className={styles.grid}>
            <FormField label="Nome" htmlFor="nome" required error={errors.nome?.message}>
              <Input
                id="nome"
                invalid={Boolean(errors.nome)}
                disabled={!podeEditar}
                {...register('nome')}
              />
            </FormField>

            <FormField label="Código do prestador" htmlFor="codigoPrestador">
              <Input id="codigoPrestador" disabled={!podeEditar} {...register('codigoPrestador')} />
            </FormField>

            <FormField label="Login" htmlFor="login">
              <Input id="login" disabled={!podeEditar} {...register('login')} />
            </FormField>

            <FormField
              label="Senha"
              htmlFor="senha"
              hint="Alterar a senha afeta imediatamente a autenticação do crawler no portal da operadora."
            >
              <PasswordInput id="senha" disabled={!podeEditar} {...register('senha')} />
            </FormField>

            {tipo?.hasCpf ? (
              <FormField label="CPF" htmlFor="cpf" required error={errors.cpf?.message}>
                <Input
                  id="cpf"
                  invalid={Boolean(errors.cpf)}
                  disabled={!podeEditar}
                  {...register('cpf')}
                />
              </FormField>
            ) : null}

            {tipo?.hasCnpj ? (
              <FormField label="CNPJ" htmlFor="cnpj" required error={errors.cnpj?.message}>
                <Input
                  id="cnpj"
                  invalid={Boolean(errors.cnpj)}
                  disabled={!podeEditar}
                  {...register('cnpj')}
                />
              </FormField>
            ) : null}

            {tipo?.hasTipoLogin ? (
              <FormField
                label="Tipo de login"
                htmlFor="tipoLogin"
                required
                error={errors.tipoLogin?.message}
              >
                <Input
                  id="tipoLogin"
                  invalid={Boolean(errors.tipoLogin)}
                  disabled={!podeEditar}
                  {...register('tipoLogin')}
                />
              </FormField>
            ) : null}

            {tipo?.hasUnidadeLogin ? (
              <FormField
                label="Unidade de login"
                htmlFor="unidadeLogin"
                required
                error={errors.unidadeLogin?.message}
              >
                <Input
                  id="unidadeLogin"
                  invalid={Boolean(errors.unidadeLogin)}
                  disabled={!podeEditar}
                  {...register('unidadeLogin')}
                />
              </FormField>
            ) : null}

            <FormField label="Nome do contratado" htmlFor="nomeContratado">
              <Input id="nomeContratado" disabled={!podeEditar} {...register('nomeContratado')} />
            </FormField>
          </div>

          <FormField label="Observações" htmlFor="observacoes">
            <Textarea id="observacoes" disabled={!podeEditar} {...register('observacoes')} />
          </FormField>

          <Checkbox
            id="visivel"
            label="Visível para o cliente"
            disabled={!podeEditar}
            {...register('visivel')}
          />
        </>
      )}
    </CredencialForm>
  )
}

export default DadosGeraisTab
